import { Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/app/auth/stores/auth.store';
import { AppLayout } from '@/app/layouts/AppLayout';
import { activeModules } from '@/app/modules';
import type { RouteHandle } from '@/shared/types/module';
import { Loader2 } from 'lucide-react';
import { AuthLoginPage } from '@/app/auth/pages/login/AuthLoginPage';
import {
  clearRedirectTarget,
  readRedirectTarget,
  storeRedirectTarget,
} from '@/app/auth/lib/redirect';

const LoadingFallback = () => (
  <div className="flex items-center justify-center py-12 text-ink-muted">
    <Loader2 className="size-5 animate-spin" />
  </div>
);

const RootRoute = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  return <Navigate replace to={isAuthenticated ? '/dashboard' : '/login'} />;
};

const RouteGuard = ({
  handle,
  children,
}: {
  handle: RouteHandle | undefined;
  children: ReactNode;
}) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const authorized = useAuthStore((s) =>
    handle?.roles ? handle.roles.some((r) => s.hasRole(r)) : true,
  );
  const location = useLocation();

  if (handle?.guest) {
    return isAuthenticated ? <Navigate replace to={readRedirectTarget()} /> : children;
  }

  if (handle?.auth || handle?.roles) {
    if (!isAuthenticated) {
      storeRedirectTarget(location.pathname + location.search);

      return <Navigate replace to="/login" />;
    }
    clearRedirectTarget();
    if (handle.roles && !authorized) {
      return <Navigate replace to="/dashboard" />;
    }
  }

  return children;
};

const getRouteLayout = (route: (typeof activeModules)[number]['routes'][number]) => {
  const handle: unknown = route.handle;

  if (typeof handle !== 'object' || handle === null || !('layout' in handle)) {
    return undefined;
  }

  const { layout } = handle as { layout?: unknown };

  return layout === 'app' ? layout : undefined;
};

const withConfiguredLayout = (
  route: (typeof activeModules)[number]['routes'][number],
  module: (typeof activeModules)[number],
) => {
  let element = route.element;

  if (module.providers?.length) {
    element = module.providers.reduceRight<ReactNode>(
      (acc, Provider) => <Provider>{acc}</Provider>,
      element,
    );
  }

  element = <Suspense fallback={<LoadingFallback />}>{element}</Suspense>;

  if (getRouteLayout(route) === 'app') {
    element = <AppLayout>{element}</AppLayout>;
  }

  element = <RouteGuard handle={route.handle as RouteHandle | undefined}>{element}</RouteGuard>;

  return { ...route, element };
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRoute />,
  },
  {
    path: '/login',
    element: (
      <RouteGuard handle={{ guest: true }}>
        <AuthLoginPage />
      </RouteGuard>
    ),
  },
  ...activeModules.flatMap((module) =>
    module.routes.map((route) => withConfiguredLayout(route, module)),
  ),
]);
